/**
 * REMOTE PLAYER VISUALS.
 *
 * Renders the other humans in the match using the same skinned soldiers,
 * skeleton and animation solver the single-player AI uses — so a networked
 * opponent looks exactly as good as an offline one — but driven entirely by
 * snapshots instead of by `src/ai`'s decision making.
 *
 * The `ai` subsystem is reached through `ctx.get('ai')` for one thing only: the
 * cached soldier variants (geometry, materials, weapon prop). Those are already
 * patched into the renderer's shadow/GTAO path by whoever built them, which is
 * why borrowing them beats rebuilding a character here.
 */
import * as THREE from 'three';
import { RIG } from '../ai/rig.js';
import { Animator } from '../ai/animator.js';
import { TEAM } from '../net/protocol.js';

/** CTs get the uniformed look, Ts the irregular one. Instant team readability. */
const VARIANT_FOR_TEAM = {
  [TEAM.CT]: 'vanguard',
  [TEAM.T]: 'irregular',
  [TEAM.NONE]: 'breacher',
};

/** How far behind the newest snapshot we render, in seconds. */
const INTERP_DELAY = 0.066; // two snapshots at 30 Hz

export class RemotePlayers {
  constructor(ctx) {
    this.ctx = ctx;
    this.root = new THREE.Group();
    this.root.name = 'remotePlayers';
    this.root.matrixAutoUpdate = false;
    ctx.scene.add(this.root);

    this.actors = new Map();      // id -> RemoteActor
    this.variants = new Map();    // team -> variant bundle (shared geometry)
    this.materials = null;

    // Scratch — nothing in the per-frame path allocates.
    this._v = new THREE.Vector3();
    this._aim = new THREE.Vector3();
    this._look = new THREE.Vector3();
  }

  /* ─────────────────────────── setup ─────────────────────────── */

  init() {
    const ai = this.ctx.peek('ai');
    this.ai = ai ?? null;
    if (ai) {
      for (const team of [TEAM.T, TEAM.CT, TEAM.NONE]) {
        const name = VARIANT_FOR_TEAM[team];
        try {
          this.variants.set(team, ai.variant(name));
        } catch (err) {
          console.warn(`[remote] no soldier variant "${name}"`, err?.message);
        }
      }
    }
    this.enabled = this.variants.size > 0;
    if (!this.enabled) {
      console.warn('[remote] ai subsystem unavailable — remote players will not be drawn');
    }
    return this;
  }

  /* ─────────────────────────── per-actor ─────────────────────────── */

  ensure(id) {
    let a = this.actors.get(id);
    if (a) return a;
    a = new RemoteActor(this, id);
    this.actors.set(id, a);
    return a;
  }

  remove(id) {
    const a = this.actors.get(id);
    if (!a) return;
    a.dispose();
    this.actors.delete(id);
  }

  /** Called for every player record in a snapshot. */
  /**
   * Called for every player record in a snapshot, in packed wire form
   * (short keys — see protocol.packPlayer). The local player is filtered out
   * here so the client never renders a ghost of itself.
   */
  feed(rec, now) {
    if (rec.i === this.localId) return;
    this.ensure(rec.i).feed(rec, now);
  }

  /** Drop anyone the server no longer lists. */
  prune(ids) {
    for (const id of [...this.actors.keys()]) if (!ids.has(id)) this.remove(id);
  }

  /* ─────────────────────────── frame ─────────────────────────── */

  update(dt, now, localId) {
    this.localId = localId;
    for (const a of this.actors.values()) a.update(dt, now);
  }

  dispose() {
    for (const id of [...this.actors.keys()]) this.remove(id);
    this.ctx.scene.remove(this.root);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */

class RemoteActor {
  constructor(owner, id) {
    this.owner = owner;
    this.id = id;
    this.ctx = owner.ctx;

    /** Snapshot history for interpolation: newest at the end. */
    this.buf = [];
    this.team = TEAM.NONE;
    this.alive = true;
    this.speed = 0;
    this.stance = 0;
    this.ads = 0;
    this.hp = 100;
    this.teamSet = false;

    this.group = new THREE.Group();
    this.group.name = `remote${id}`;
    this.group.visible = false;
    owner.root.add(this.group);

    this.mesh = null;
    this.animator = null;
    this.bones = null;
    this.skeleton = null;
    this.root_bone = null;

    // Interpolated render state.
    this.rx = 0; this.ry = 0; this.rz = 0;
    this.ryaw = 0; this.rpitch = 0;
    this._havePose = false;

    this._animAccum = 0;
    this._lastFireAt = -1;
    this._deathT = 0;
  }

  /** Build the skinned body the first time we know this player's team. */
  _build(team) {
    const bundle = this.owner.variants.get(team) ?? this.owner.variants.get(TEAM.NONE);
    if (!bundle) return false;
    const { geometry, materials, weapon } = bundle;

    const { bones, skeleton, root } = RIG.createSkeleton();
    this.bones = bones;
    this.skeleton = skeleton;
    this.root_bone = root;

    this.mesh = new THREE.SkinnedMesh(geometry, materials);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = true;
    this.mesh.bind(skeleton);

    this.group.add(root);
    this.group.add(this.mesh);

    this.animator = new Animator(RIG, bones, {
      weapon,
      rng: this.ctx.rng.fork(),
      scale: bundle.variant?.scale ?? 1,
      probe: (x, z, fromY, out) => this._probeGround(x, z, fromY, out),
    });
    this.teamSet = true;
    return true;
  }

  _probeGround(x, z, fromY, out) {
    const phys = this.ctx.peek('physics');
    if (!phys) return false;
    const h = phys.raycast(x, fromY, z, 0, -1, 0, 3.2, phys.MASK.WORLD);
    if (!h.hit) return false;
    out.y = h.point.y;
    out.nx = h.normal.x; out.ny = h.normal.y; out.nz = h.normal.z;
    out.hit = true;
    return true;
  }

  /** Called for every player record in a snapshot, in packed wire form. */
  feed(rec, now) {
    if (!this.teamSet && rec.tm !== undefined) {
      this.team = rec.tm;
      if (!this._build(rec.tm)) return;
    }
    this.team = rec.tm ?? this.team;
    this.hp = rec.hp ?? this.hp;
    this.stance = rec.st ?? 0;
    this.ads = (rec.ad ?? 0) / 255;
    this.alive = rec.al === 1;

    const b = this.buf;
    b.push({
      t: now,
      x: rec.x, y: rec.y, z: rec.z,
      vx: rec.vx ?? 0, vy: rec.vy ?? 0, vz: rec.vz ?? 0,
      yaw: rec.w ?? 0, pitch: rec.p ?? 0,
      alive: this.alive,
    });
    // Keep ~1 s of history; anything older can never be interpolated to.
    while (b.length > 40) b.shift();

    if (!this._havePose) {
      this.rx = rec.x; this.ry = rec.y; this.rz = rec.z;
      this.ryaw = rec.w ?? 0; this.rpitch = rec.p ?? 0;
      this._havePose = true;
    }
  }

  update(dt, now) {
    if (!this.teamSet) return;
    const b = this.buf;
    const renderTime = now - INTERP_DELAY;

    if (b.length >= 2) {
      // Find the pair straddling renderTime. Keep `i` at >= 1 so that both
      // `a` and `c` stay inside the buffer: when renderTime is older than every
      // sample (the first ~66 ms after a remote appears, or whenever buffered
      // history is shorter than the interpolation delay) the pair clamps to the
      // two oldest samples instead of indexing `b[-1]`.
      let i = b.length - 1;
      while (i > 1 && b[i - 1].t > renderTime) i--;
      const a = b[i - 1], c = b[i];
      const span = c.t - a.t;
      const alpha = span > 1e-4 ? Math.min(1, Math.max(0, (renderTime - a.t) / span)) : 1;

      // Extrapolate a little if the next snapshot is late, so a dropped packet
      // does not make a running player stutter to a halt.
      const ex = span > 1e-4 ? Math.min(0.25, (now - c.t)) : 0;

      this.rx = a.x + (c.x - a.x) * alpha + c.vx * ex;
      this.ry = a.y + (c.y - a.y) * alpha + c.vy * ex;
      this.rz = a.z + (c.z - a.z) * alpha + c.vz * ex;
      this.ryaw = lerpAngle(a.yaw, c.yaw, alpha);
      this.rpitch = a.pitch + (c.pitch - a.pitch) * alpha;
      this.speed = Math.hypot(c.vx, c.vz);
    } else if (b.length === 1) {
      this.rx = b[0].x; this.ry = b[0].y; this.rz = b[0].z;
      this.ryaw = b[0].yaw; this.rpitch = b[0].pitch;
      this.speed = Math.hypot(b[0].vx, b[0].vz);
    } else {
      return;
    }

    this.group.visible = true;
    this.group.position.set(this.rx, this.ry, this.rz);
    this.group.rotation.y = this.ryaw;
    this.group.updateMatrixWorld(true);

    // A dead body drops and stops animating; keep it on the floor for a few
    // seconds so the kill is legible, then hide it.
    if (!this.alive) {
      this.group.rotation.x = -Math.PI / 2;
      this.group.position.y += 0.12;
      this._deathT += dt;
      if (this._deathT > 6) this.group.visible = false;
      if (this.animator) this.animator.enabled = false;
      return;
    }
    this.group.rotation.x = 0;
    this._deathT = 0;
    if (this.animator) this.animator.enabled = true;

    const clip = pickClip(this.stance, this.speed, this.hp);
    const an = this.animator;
    if (!an) return;

    // Aim target from the networked view angles, 20 m ahead.
    const cp = Math.cos(this.rpitch);
    this.owner._aim.set(
      this.rx - Math.sin(this.ryaw) * cp * 20,
      this.ry + 1.62 + Math.sin(this.rpitch) * 20,
      this.rz - Math.cos(this.ryaw) * cp * 20
    );
    this.owner._look.copy(this.owner._aim);

    an.setState({
      clip,
      speed: this.speed,
      crouch: this.stance === 1,
      aimTarget: this.owner._aim,
      lookTarget: this.owner._look,
      aimWeight: Math.max(this.ads, this.speed > 0.2 ? 0.35 : 0),
      suppress: 0,
    });

    this._animAccum += dt;
    an.update(this._animAccum, now);
    this._animAccum = 0;
  }

  /** Cosmetic: the shot's muzzle flash and recoil, driven by the FIRE event. */
  playFire(strength = 1) {
    this.animator?.fire(strength);
  }

  playHit(region = 'torso', side = 1, strength = 1) {
    this.animator?.hit(region, side, strength);
  }

  dispose() {
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh = null;
    }
    if (this.root_bone) this.group.remove(this.root_bone);
    this.owner.root.remove(this.group);
    this.animator = null;
    this.buf.length = 0;
  }
}

const lerpAngle = (a, b, t) => {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};

function pickClip(stance, speed, hp) {
  const moving = speed > 0.25;
  if (stance === 1) return moving ? 'crouchWalk' : 'crouchIdle';
  if (stance === 2) return moving ? 'crouchWalk' : 'crouchIdle';
  if (speed > 2.6) return 'run';
  if (moving) return 'walk';
  return hp < 35 ? 'hurtIdle' : 'idle';
}
